// Centralized address validation utilities for all supported blockchains
// Reuses existing validators and provides format hints

import { isValidEVMAddress } from './liquidityProviders';
import { isValidRavencoinAddress } from './ravencoinAssetParser';
import { getChainMetadata, type ChainId as StringChainId, getNumericChainId } from './chainRegistry';
import { CHAIN_IDS } from './tokenRegistry';

/**
 * Convert numeric chain ID to string chain ID for metadata lookup
 */
export function getStringChainIdFromNumeric(numericChainId: number): StringChainId | null {
  const chainIdMap: Record<number, StringChainId> = {
    1: 'ethereum',
    56: 'binanceSmartChain',
    137: 'polygon',
    1101: 'polygonZkEVM',
    43114: 'avalanche',
    42161: 'arbitrum',
    8453: 'base',
    10: 'optimism',
    998: 'hyperliquid',
    250: 'fantom',
    324: 'zkSyncEra',
    59144: 'linea',
    534352: 'scroll',
    5000: 'mantle',
    81457: 'blast',
    42220: 'celo',
    100: 'gnosis',
    34443: 'mode',
    // UTXO chains use 0 as placeholder
    0: 'ravencoin', // Special case for UTXO chains
    // Special IDs for native UTXO tokens (used for cross-chain detection)
    10000: 'ravencoin', // RVN
    10001: 'ravencoin', // BTC (mapped to ravencoin for now, as we don't have a bitcoin chain registry entry)
    10002: 'ravencoin', // LTC
    10003: 'ravencoin', // DOGE
    10004: 'ravencoin', // BCH
    10005: 'ravencoin', // ZEC
    10006: 'ravencoin', // DASH
    10009: 'ravencoin', // KAS (Kaspa) - mapped to ravencoin type for UTXO handling
    // Other chains
    9999: 'solana', // Solana
    9998: 'ripple', // XRP/Ripple
    9997: 'monero', // XMR/Monero (placeholder, not in chain registry)
  };
  
  return chainIdMap[numericChainId] || null;
}

/**
 * Get chain metadata by numeric chain ID
 */
export function getChainMetadataByNumericId(numericChainId: number) {
  const stringChainId = getStringChainIdFromNumeric(numericChainId);
  if (!stringChainId) return null;
  return getChainMetadata(stringChainId);
}

/**
 * Validate Bitcoin address
 * Bitcoin addresses can be:
 * - Legacy (P2PKH): starts with '1', 26-35 chars
 * - SegWit (P2SH): starts with '3', 26-35 chars
 * - Bech32 (P2WPKH/P2WSH): starts with 'bc1', 14-74 chars
 */
export function isValidBitcoinAddress(address: string): boolean {
  if (!address || typeof address !== 'string') return false;
  
  const trimmed = address.trim();
  
  // Legacy P2PKH
  if (/^1[a-km-zA-HJ-NP-Z1-9]{25,34}$/.test(trimmed)) {
    return true;
  }
  
  // P2SH
  if (/^3[a-km-zA-HJ-NP-Z1-9]{25,34}$/.test(trimmed)) {
    return true;
  }
  
  // Bech32 (native SegWit)
  if (/^bc1[a-z0-9]{13,62}$/i.test(trimmed)) {
    return true;
  }
  
  return false;
}

/**
 * Validate Litecoin address
 * Similar to Bitcoin but with different prefixes:
 * - Legacy: starts with 'L' or 'M'
 * - P2SH: starts with '3'
 * - Bech32: starts with 'ltc1'
 */
export function isValidLitecoinAddress(address: string): boolean {
  if (!address || typeof address !== 'string') return false;
  
  const trimmed = address.trim();
  
  // Legacy P2PKH (starts with L or M)
  if (/^[LM][a-km-zA-HJ-NP-Z1-9]{25,34}$/.test(trimmed)) {
    return true;
  }
  
  // P2SH
  if (/^3[a-km-zA-HJ-NP-Z1-9]{25,34}$/.test(trimmed)) {
    return true;
  }
  
  // Bech32 (native SegWit)
  if (/^ltc1[a-z0-9]{13,62}$/i.test(trimmed)) {
    return true;
  }
  
  return false;
}

/**
 * Validate Dogecoin address
 * Dogecoin addresses start with 'D' and are 34 characters long
 */
export function isValidDogecoinAddress(address: string): boolean {
  if (!address || typeof address !== 'string') return false;
  
  const trimmed = address.trim();
  
  // Dogecoin addresses start with 'D' and are 34 chars
  if (/^D[a-km-zA-HJ-NP-Z1-9]{33}$/.test(trimmed)) {
    return true;
  }
  
  return false;
}

/**
 * Validate Kaspa address
 * Kaspa addresses use bech32 encoding with "kaspa:" prefix
 * Format: kaspa: followed by bech32 encoded address (typically 62-64 characters total)
 */
export function isValidKaspaAddress(address: string): boolean {
  if (!address || typeof address !== 'string') return false;
  
  const trimmed = address.trim();
  
  // Kaspa addresses start with "kaspa:" prefix followed by bech32 encoded address
  // The bech32 part can vary in length (typically 40-70 characters)
  // Total length is typically 46-76 characters (kaspa: + bech32 part)
  if (/^kaspa:[a-z0-9]{40,70}$/i.test(trimmed)) {
    return true;
  }
  
  return false;
}

/**
 * Validate Solana address
 * Solana addresses are base58-encoded and 32-44 characters long
 */
export function isValidSolanaAddress(address: string): boolean {
  if (!address || typeof address !== 'string') return false;
  
  const trimmed = address.trim();
  
  // Solana addresses are base58, typically 32-44 chars
  // Base58 pattern: no 0, O, I, l
  if (/^[1-9A-HJ-NP-Za-km-z]{32,44}$/.test(trimmed)) {
    return true;
  }
  
  return false;
}

/**
 * Validate ICP Principal ID
 * ICP Principal IDs are base32-encoded and start with specific prefixes
 */
export function isValidICPPrincipal(address: string): boolean {
  if (!address || typeof address !== 'string') return false;
  
  const trimmed = address.trim();
  
  // ICP Principal IDs are base32-encoded, typically 53-64 chars
  // Format: starts with letter, followed by base32 chars (a-z, 2-7)
  if (/^[a-z][a-z2-7]{52,63}$/i.test(trimmed)) {
    return true;
  }
  
  return false;
}

/**
 * Validate TON address
 * TON addresses are base64-encoded and typically start with:
 * - 'EQ' for workchain 0 (masterchain)
 * - 'UQ' for workchain 0 (user-friendly)
 * Format: Base64, typically 48 characters (EQ/UQ + 46 base64 chars)
 * Can also include underscores and hyphens in some formats
 */
export function isValidTONAddress(address: string): boolean {
  if (!address || typeof address !== 'string') return false;
  
  const trimmed = address.trim();
  
  // TON addresses are base64-encoded, typically 48 chars
  // Can start with EQ (workchain 0) or UQ (user-friendly)
  // Base64 pattern: A-Z, a-z, 0-9, +, /, = (padding), _, - (some formats)
  // Length: typically 48 chars (EQ/UQ + 46 base64 chars)
  if (/^(EQ|UQ)[A-Za-z0-9+/_\-=]{44,}$/.test(trimmed) && trimmed.length >= 46 && trimmed.length <= 50) {
    return true;
  }
  
  return false;
}

/**
 * Validate SUI address
 * SUI addresses are 32-byte hex strings (0x + 64 hex characters)
 * Format: 0x followed by exactly 64 hexadecimal characters
 */
export function isValidSUIAddress(address: string): boolean {
  if (!address || typeof address !== 'string') return false;
  
  const trimmed = address.trim();
  
  // SUI addresses are 0x followed by exactly 64 hex characters
  if (/^0x[a-fA-F0-9]{64}$/.test(trimmed)) {
    return true;
  }
  
  return false;
}

/**
 * Validate Zcash address
 * Zcash addresses can be:
 * - Transparent: starts with 't1' (P2PKH) or 't3' (P2SH), typically 35 characters
 * - Shielded: starts with 'zs1' (Sapling) or 'z' (Sprout), longer
 */
export function isValidZcashAddress(address: string): boolean {
  if (!address || typeof address !== 'string') return false;
  
  const trimmed = address.trim();
  
  // Transparent addresses: t1... or t3... (typically 35 chars)
  if (/^t[13][a-km-zA-HJ-NP-Z1-9]{25,34}$/.test(trimmed)) {
    return true;
  }
  
  // Shielded Sapling addresses: zs1... (typically 78 chars)
  if (/^zs1[a-z0-9]{75,}$/.test(trimmed)) {
    return true;
  }
  
  // Shielded Sprout addresses: z... (typically 78 chars)
  if (/^z[a-z0-9]{75,}$/.test(trimmed)) {
    return true;
  }
  
  return false;
}

/**
 * Validate Monero address
 * Monero addresses are 95 characters long and start with:
 * - '4' for mainnet standard addresses
 * - '8' for subaddresses (integrated addresses also start with 4 or 8)
 * Format: Base58-encoded, exactly 95 characters
 */
export function isValidMoneroAddress(address: string): boolean {
  if (!address || typeof address !== 'string') return false;
  
  const trimmed = address.trim();
  
  // Monero addresses are exactly 95 characters
  // Start with '4' (mainnet standard/integrated) or '8' (subaddress)
  // Base58 characters: 1-9, A-H, J-N, P-Z, a-k, m-z (no 0, O, I, l)
  if (/^[48][1-9A-HJ-NP-Za-km-z]{94}$/.test(trimmed)) {
    return true;
  }
  
  return false;
}

/**
 * Validate address for a specific chain
 * @param address - Address to validate
 * @param chainId - Numeric chain ID or string chain ID
 * @param tokenSymbol - Optional token symbol for special cases (e.g., XMR for Monero)
 * @returns true if address is valid for the chain
 */
export function validateAddressForChain(
  address: string,
  chainId: number | StringChainId,
  tokenSymbol?: string
): boolean {
  if (!address || typeof address !== 'string') return false;
  
  // Special case: Monero (XMR) - privacy coin with unique address format
  // Monero doesn't have a chain ID in our registry, so check by token symbol
  if (tokenSymbol?.toUpperCase() === 'XMR') {
    return isValidMoneroAddress(address);
  }
  
  // Special case: Bitcoin (BTC) - check by token symbol since BTC chain ID maps to ravencoin
  if (tokenSymbol?.toUpperCase() === 'BTC') {
    return isValidBitcoinAddress(address);
  }
  
  // Special case: Litecoin (LTC)
  if (tokenSymbol?.toUpperCase() === 'LTC') {
    return isValidLitecoinAddress(address);
  }
  
  // Special case: Dogecoin (DOGE)
  if (tokenSymbol?.toUpperCase() === 'DOGE') {
    return isValidDogecoinAddress(address);
  }
  
  // Special case: Kaspa (KAS)
  if (tokenSymbol?.toUpperCase() === 'KAS') {
    return isValidKaspaAddress(address);
  }
  
  // Special case: Zcash (ZEC) - privacy coin with unique address format
  if (tokenSymbol?.toUpperCase() === 'ZEC') {
    return isValidZcashAddress(address);
  }
  
  // Special case: Solana (SOL) - check by token symbol since chain ID might not be set
  if (tokenSymbol?.toUpperCase() === 'SOL') {
    return isValidSolanaAddress(address);
  }
  
  // Special case: TON - check by token symbol
  if (tokenSymbol?.toUpperCase() === 'TON') {
    return isValidTONAddress(address);
  }
  
  // Special case: SUI - check by token symbol
  if (tokenSymbol?.toUpperCase() === 'SUI') {
    return isValidSUIAddress(address);
  }
  
  // Convert to numeric if needed
  let numericChainId: number;
  if (typeof chainId === 'string') {
    const converted = getNumericChainId(chainId);
    if (converted === null) return false;
    numericChainId = converted;
  } else {
    numericChainId = chainId;
  }
  
  // Get chain metadata to determine chain type
  const metadata = getChainMetadataByNumericId(numericChainId);
  if (!metadata) {
    // Fallback: try to infer from chain ID
    if (numericChainId === CHAIN_IDS.ETHEREUM || 
        numericChainId === CHAIN_IDS.BASE || 
        numericChainId === CHAIN_IDS.BSC ||
        numericChainId === CHAIN_IDS.HYPERLIQUID) {
      return isValidEVMAddress(address);
    }
    // Check for UTXO chain IDs (10000-10006)
    if (numericChainId >= 10000 && numericChainId <= 10006) {
      // Try all UTXO address formats
      return isValidBitcoinAddress(address) || 
             isValidLitecoinAddress(address) || 
             isValidDogecoinAddress(address) ||
             isValidRavencoinAddress(address);
    }
    return false;
  }
  
  // Route to appropriate validator based on chain type
  switch (metadata.type) {
    case 'EVM':
      return isValidEVMAddress(address);
    
    case 'UTXO':
      // Determine which UTXO chain - check token symbol first for accuracy
      if (tokenSymbol?.toUpperCase() === 'BTC') {
        return isValidBitcoinAddress(address);
      }
      if (tokenSymbol?.toUpperCase() === 'LTC') {
        return isValidLitecoinAddress(address);
      }
      if (tokenSymbol?.toUpperCase() === 'DOGE') {
        return isValidDogecoinAddress(address);
      }
      if (tokenSymbol?.toUpperCase() === 'KAS') {
        return isValidKaspaAddress(address);
      }
      if (metadata.id === 'ravencoin' || tokenSymbol?.toUpperCase() === 'RVN') {
        return isValidRavencoinAddress(address);
      }
      // For other UTXO chains, try all UTXO formats
      return isValidBitcoinAddress(address) || 
             isValidLitecoinAddress(address) || 
             isValidDogecoinAddress(address) ||
             isValidRavencoinAddress(address) ||
             isValidKaspaAddress(address);
    
    case 'OTHER':
      if (metadata.id === 'solana') {
        return isValidSolanaAddress(address);
      }
      // Monero is a special case - privacy coin with unique address format
      // Check if this is a Monero address (95 chars, starts with 4 or 8)
      if (isValidMoneroAddress(address)) {
        return true;
      }
      // ICP would be handled here if added
      return false;
    
    default:
      return false;
  }
}

/**
 * Get address format hint for a specific chain
 * @param chainId - Numeric chain ID or string chain ID
 * @returns Format hint string
 */
export function getAddressFormatForChain(
  chainId: number | StringChainId
): string {
  // Convert to numeric if needed
  let numericChainId: number;
  if (typeof chainId === 'string') {
    const converted = getNumericChainId(chainId);
    if (converted === null) return 'Unknown address format';
    numericChainId = converted;
  } else {
    numericChainId = chainId;
  }
  
  // Get chain metadata
  const metadata = getChainMetadataByNumericId(numericChainId);
  if (!metadata) {
    // Fallback hints
    if (numericChainId === CHAIN_IDS.ETHEREUM || 
        numericChainId === CHAIN_IDS.BASE || 
        numericChainId === CHAIN_IDS.BSC ||
        numericChainId === CHAIN_IDS.HYPERLIQUID) {
      return 'EVM address (0x...)';
    }
    return 'Unknown address format';
  }
  
  // Return format hints based on chain type
  switch (metadata.type) {
    case 'EVM':
      return `EVM address (0x followed by 40 hex characters)`;
    
    case 'UTXO':
      if (metadata.id === 'ravencoin') {
        return 'Ravencoin address (starts with R, 34 characters)';
      }
      return 'UTXO address (varies by chain)';
    
    case 'OTHER':
      if (metadata.id === 'solana') {
        return 'Solana address (base58, 32-44 characters)';
      }
      return 'Chain-specific address format';
    
    default:
      return 'Unknown address format';
  }
}

/**
 * Get address format examples for a specific chain
 * @param chainId - Numeric chain ID or string chain ID
 * @returns Array of example addresses (for display only)
 */
export function getAddressFormatExamples(
  chainId: number | StringChainId
): string[] {
  // Convert to numeric if needed
  let numericChainId: number;
  if (typeof chainId === 'string') {
    const converted = getNumericChainId(chainId);
    if (converted === null) return [];
    numericChainId = converted;
  } else {
    numericChainId = chainId;
  }
  
  // Get chain metadata
  const metadata = getChainMetadataByNumericId(numericChainId);
  if (!metadata) return [];
  
  // Return examples based on chain type
  switch (metadata.type) {
    case 'EVM':
      return ['0x742d35Cc6634C0532925a3b844Bc9e7595f0bEb'];
    
    case 'UTXO':
      if (metadata.id === 'ravencoin') {
        return ['R9z5PyfRyBzTZrph3QyL3rJzqF8N2vK1mP'];
      }
      return ['Example UTXO address'];
    
    case 'OTHER':
      if (metadata.id === 'solana') {
        return ['9WzDXwBbmkg8ZTbNMqUxvQRAyrZzDsGYdLVL9zYtAWWM'];
      }
      return [];
    
    default:
      return [];
  }
}

